/* eslint-disable no-undef */

/**
 * ModalVideo - Modal-specific video player with Shadow DOM
 * Extends the base VideoPlayer class with modal functionality
 */
class ModalVideo extends HTMLElement {
  constructor () {
    super()
    this.videoPlayer = null

    // Create shadow DOM
    this.attachShadow({ mode: 'open' })
    this.render()
  }

  render () {
    const posterImage = this.querySelector('img')?.src || ''

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
          background: rgb(107 114 128 / 0.7);
          padding-bottom: 75%;
          border-radius: 0.125rem;
        }

        .modal-video {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        .video-poster {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 0.125rem;
          z-index: 10;
          transition: opacity 0.3s ease;
        }

        video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 0.125rem;
          z-index: 0;
        }

        .progress-bar-container {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 5px;
          background: rgb(107 114 128 / 0.3);
          z-index: 20;
          transition: all 0.3s ease;
          cursor: pointer;
          touch-action: manipulation;
        }

        :host(:hover) .progress-bar-container {
          height: 15px;
        }

        .video-progress-bar {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background: #f97316;
          transform-origin: left;
          transform: scaleX(0);
        }

        .progress-handle {
          position: absolute;
          top: 50%;
          transform: translateX(-50%) translateY(-50%);
          width: 24px;
          height: 24px;
          background: white;
          border: 2px solid #f97316;
          border-radius: 50%;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          opacity: 0;
          transition: opacity 0.2s ease, width 0.2s ease, height 0.2s ease;
          z-index: 10;
          cursor: grab;
        }

        .progress-handle:active {
          cursor: grabbing;
        }

        :host(:hover) .progress-handle {
          opacity: 1;
        }

        .mute-btn, .fullscreen-btn {
          position: absolute;
          bottom: 24px;
          width: 40px;
          height: 40px;
          background: rgb(255 255 255 / 0.8);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 30;
          opacity: 0;
          transition: all 0.2s ease;
          touch-action: manipulation;
          border: none;
          cursor: pointer;
        }

        .mute-btn:hover, .fullscreen-btn:hover {
          background: white;
        }

        :host(:hover) .mute-btn,
        :host(:hover) .fullscreen-btn {
          opacity: 1;
        }

        .mute-btn {
          right: 64px;
        }

        .fullscreen-btn {
          right: 16px;
        }

        .video-control-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 48px;
          height: 48px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
          z-index: 20;
          opacity: 0;
          transition: all 0.3s ease;
          touch-action: manipulation;
          border: none;
          cursor: pointer;
        }

        .video-control-btn:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }

        :host(.video-not-started) .video-control-btn {
          opacity: 1;
        }

        :host(:hover) .video-control-btn {
          opacity: 1;
        }

        .control-icon {
          width: 36px;
          height: 36px;
          color: black;
        }

        .control-icon.pause-icon {
          width: 28px;
          height: 28px;
        }

        .control-icon.loading-icon {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .hidden {
          display: none !important;
        }

        .mute-icon {
          width: 24px;
          height: 24px;
          color: black;
        }

        .fullscreen-icon {
          width: 24px;
          height: 24px;
          color: black;
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      </style>

      <div class="modal-video">
        ${posterImage ? `<img src="${posterImage}" alt="" class="video-poster">` : ''}
        <video></video>
        
        <div class="progress-bar-container">
          <div class="video-progress-bar">
            <span class="sr-only">Video progress</span>
          </div>
          <div class="progress-handle">
            <span class="sr-only">Drag to seek</span>
          </div>
        </div>
        
        <button type="button" class="mute-btn" aria-label="Mute video">
          <div class="mute-icon volume-on">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
            </svg>
          </div>
          <div class="mute-icon volume-off hidden">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 9.75 19.5 12m0 0 2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6 4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
            </svg>
          </div>
        </button>
        
        <button type="button" class="fullscreen-btn" aria-label="Enter fullscreen">
          <div class="fullscreen-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
            </svg>
          </div>
        </button>
        
        <button type="button" class="video-control-btn" aria-label="Play video">
          <div class="control-icon loading-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
            </svg>
          </div>
          <div class="control-icon play-icon hidden">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fill="currentColor" d="M8 19V5l11 7z"></path>
            </svg>
          </div>
          <div class="control-icon pause-icon hidden">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
            </svg>
          </div>
        </button>
      </div>
    `
  }

  connectedCallback () {
    this.setAttribute('tabindex', '0')

    // Initialize the base video player with shadow DOM context
    if (window.Tentbox?.VideoPlayer) {
      // Create a custom VideoPlayer that knows about shadow DOM
      this.videoPlayer = new ShadowDOMVideoPlayer(this)
      this.videoPlayer.initialize()
    } else {
      console.error('Tentbox.VideoPlayer not found - make sure global.js is loaded first')
    }

    // Restore state from attributes
    if (this.getAttribute('data-video-loaded') === 'true') {
      this.videoPlayer.videoLoaded = true
    }
    if (this.getAttribute('data-has-been-played') === 'true') {
      this.videoPlayer.hasBeenPlayed = true
    }
    if (this.getAttribute('data-was-paused-by-user') === 'true') {
      this.videoPlayer.wasPausedByUser = true
    }

    // Auto-play video when modal opens (modal-specific behavior)
    setTimeout(() => {
      this.loadVideoAndPlay()
    }, 100)
  }

  disconnectedCallback () {
    if (this.videoPlayer) {
      this.videoPlayer.destroy()
    }
  }

  // Modal-specific: Auto-load and play
  loadVideoAndPlay () {
    if (this.videoPlayer && typeof this.videoPlayer.loadVideo === 'function') {
      this.videoPlayer.loadVideo()

      // Wait for video to load then auto-play
      const checkAndPlay = () => {
        if (this.videoPlayer.videoLoaded && typeof this.videoPlayer.playVideo === 'function') {
          this.videoPlayer.playVideo()
        } else if (!this.videoPlayer.videoLoaded && !this.videoPlayer.isLoading) {
          setTimeout(checkAndPlay, 100)
        }
      }
      setTimeout(checkAndPlay, 200)
    }
  }

  // Expose base video player methods for external use
  loadVideo () {
    return this.videoPlayer?.loadVideo()
  }

  playVideo () {
    return this.videoPlayer?.playVideo()
  }

  pauseVideo () {
    return this.videoPlayer?.pauseVideo()
  }

  // Getters for compatibility
  get videoLoaded () {
    return this.videoPlayer?.videoLoaded || false
  }

  get isLoading () {
    return this.videoPlayer?.isLoading || false
  }

  get isPlaying () {
    return this.videoPlayer?.isPlaying || false
  }
}

/**
 * ShadowDOMVideoPlayer - Extends base VideoPlayer to work with Shadow DOM
 */
class ShadowDOMVideoPlayer extends Tentbox.VideoPlayer {
  constructor (element) {
    super(element)
    this.shadowRoot = element.shadowRoot
  }

  // Override element selection to use shadow DOM
  setupControlButton () {
    const controlBtn = this.shadowRoot.querySelector('.video-control-btn')
    if (controlBtn) {
      controlBtn.addEventListener('click', this.handleControlClick.bind(this))
      controlBtn.addEventListener('touchend', this.handleControlClick.bind(this), { passive: true })
    }
  }

  setupMuteButton () {
    const muteBtn = this.shadowRoot.querySelector('.mute-btn')
    if (muteBtn) {
      muteBtn.addEventListener('click', this.handleMuteClick.bind(this))
    }
  }

  setupFullscreenButton () {
    const fullscreenBtn = this.shadowRoot.querySelector('.fullscreen-btn')
    if (fullscreenBtn) {
      fullscreenBtn.addEventListener('click', this.handleFullscreenClick.bind(this))
    }
  }

  setupProgressBarSeeking () {
    const progressContainer = this.shadowRoot.querySelector('.progress-bar-container')
    if (progressContainer) {
      progressContainer.addEventListener('click', this.handleProgressBarClick.bind(this))
      progressContainer.addEventListener('touchstart', this.handleProgressDragStart.bind(this), { passive: false })
      progressContainer.addEventListener('mousedown', this.handleProgressDragStart.bind(this))
    }

    // Set up document-level drag event listeners
    document.addEventListener('touchmove', this.handleProgressDragMove.bind(this), { passive: false })
    document.addEventListener('touchend', this.handleProgressDragEnd.bind(this))
    document.addEventListener('mousemove', this.handleProgressDragMove.bind(this))
    document.addEventListener('mouseup', this.handleProgressDragEnd.bind(this))
  }

  setupKeyboardControls () {
    this.element.addEventListener('keydown', this.handleKeyDown.bind(this))
  }

  setupVideoContainerClick () {
    // Modal videos don't need container click behavior
  }

  // Override progress bar initialization
  initialize () {
    this.setupControlButton()
    this.setupMuteButton()
    this.setupFullscreenButton()
    this.setupProgressBarSeeking()
    this.progressBar = this.shadowRoot.querySelector('.video-progress-bar')
    this.setupKeyboardControls()
    this.setupVideoContainerClick()

    // Restore state from attributes if available
    if (this.element.getAttribute('data-video-loaded') === 'true') {
      this.videoLoaded = true
    }
    if (this.element.getAttribute('data-has-been-played') === 'true') {
      this.hasBeenPlayed = true
    }
    if (this.element.getAttribute('data-was-paused-by-user') === 'true') {
      this.wasPausedByUser = true
    }
  }

  // Override element queries to use shadow DOM
  loadVideo () {
    if (this.videoLoaded || this.isLoading) return

    const videoElement = this.shadowRoot.querySelector('video')
    const videoSource = this.element.dataset.source

    if (videoElement && videoSource) {
      this.isLoading = true
      this.showLoadingState()

      videoElement.muted = false
      this.isMuted = false
      this.updateMuteButton()

      // Clear existing sources
      const existingSources = videoElement.querySelectorAll('source')
      existingSources.forEach(source => source.remove())

      // Determine MIME type
      let mimeType = 'video/mp4'
      if (videoSource.includes('.m3u8')) {
        mimeType = 'application/x-mpegURL'
      } else if (videoSource.includes('.webm')) {
        mimeType = 'video/webm'
      }

      const sourceElement = document.createElement('source')
      sourceElement.src = videoSource
      sourceElement.type = mimeType
      videoElement.appendChild(sourceElement)

      const handleCanPlayThrough = () => {
        this.videoLoaded = true
        this.isLoading = false
        this.element.setAttribute('data-video-loaded', 'true')
        this.showPlayState()
        this.cleanupVideoListeners(videoElement, handleCanPlayThrough, handleError)
      }

      const handleError = (e) => {
        this.isLoading = false
        console.error('Failed to load video:', videoSource, e)
        this.cleanupVideoListeners(videoElement, handleCanPlayThrough, handleError)
        this.showPlayState()
      }

      videoElement.addEventListener('canplaythrough', handleCanPlayThrough)
      videoElement.addEventListener('error', handleError)
      videoElement.load()
    }
  }

  handleControlClick () {
    const now = Date.now()
    if (now - this.lastControlClickTime < 250) {
      return
    }
    this.lastControlClickTime = now

    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement) return

    if (!this.videoLoaded && !this.isLoading) {
      this.loadVideo()
    } else if (this.videoLoaded && !this.isLoading) {
      if (this.isPlaying) {
        this.pauseVideo()
      } else {
        this.playVideo()
      }
    }
  }

  playVideo () {
    const videoElement = this.shadowRoot.querySelector('video')

    if (videoElement && this.videoLoaded && !this.isLoading && videoElement.readyState >= 4) {
      // Don't change state immediately - wait for play promise to resolve
      videoElement.play().then(() => {
        this.isPlaying = true
        this.hasBeenPlayed = true
        this.wasPausedByUser = false
        this.element.setAttribute('data-has-been-played', 'true')
        this.element.setAttribute('data-was-paused-by-user', 'false')
        this.showPauseState()
        this.hidePosterImage()
        this.startProgressUpdate()
        this.element.classList.remove('video-not-started')

        if (this.isMobileOrTablet()) {
          this.hideControlsWithDelay()
        }

        // Dispatch custom event for external listeners
        this.element.dispatchEvent(new CustomEvent('videoStateChange', {
          detail: { state: 'playing' },
        }))
      }).catch((error) => {
        console.error('Modal video play failed:', error)
        // Play failed - revert to play state without rapid flickering
        setTimeout(() => {
          this.showPlayState()
          this.element.dispatchEvent(new CustomEvent('videoStateChange', {
            detail: { state: 'ready' },
          }))
        }, 100)
      })
    } else if (!this.videoLoaded && !this.isLoading) {
      // If video isn't loaded yet, try to load it first
      this.loadVideo()
    }
  }

  pauseVideo () {
    const videoElement = this.shadowRoot.querySelector('video')

    if (videoElement && !videoElement.paused) {
      videoElement.pause()
      this.isPlaying = false
      this.wasPausedByUser = true
      this.element.setAttribute('data-was-paused-by-user', 'true')
      this.showPlayState()
      this.stopProgressUpdate()

      if (this.isMobileOrTablet()) {
        if (this.controlsHideTimeout) {
          clearTimeout(this.controlsHideTimeout)
          this.controlsHideTimeout = null
        }
        this.element.classList.remove('controls-hidden')
      }

      // Dispatch custom event for external listeners
      this.element.dispatchEvent(new CustomEvent('videoStateChange', {
        detail: { state: 'paused' },
      }))
    }
  }

  handleKeyDown (e) {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      this.handleControlClick()
    }

    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement || !this.videoLoaded) return

    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      videoElement.currentTime = Math.max(0, videoElement.currentTime - 7)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      videoElement.currentTime = Math.min(videoElement.duration, videoElement.currentTime + 5)
    }
  }

  handleMuteClick () {
    const videoElement = this.shadowRoot.querySelector('video')
    if (videoElement) {
      this.isMuted = !this.isMuted
      videoElement.muted = this.isMuted
      this.updateMuteButton()
    }
  }

  handleFullscreenClick () {
    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement) return

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    } else {
      if (videoElement.requestFullscreen) {
        videoElement.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`)
        })
      } else if (videoElement.webkitEnterFullscreen) {
        videoElement.webkitEnterFullscreen()
      }
    }
  }

  handleProgressBarClick (e) {
    if (this.isDragging) return

    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement || !this.videoLoaded) return

    const progressContainer = e.currentTarget
    const rect = progressContainer.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const percentage = clickX / rect.width
    const seekTime = videoElement.duration * percentage

    if (seekTime >= 0 && seekTime <= videoElement.duration) {
      videoElement.currentTime = seekTime
      this.updateProgressBar(percentage)
    }
  }

  handleProgressDragStart (e) {
    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement || !this.videoLoaded || this.isDragging) return

    this.isDragging = true
    e.preventDefault()
    e.stopPropagation()

    this.wasPlayingBeforeDrag = this.isPlaying

    if (this.isPlaying) {
      videoElement.pause()
      this.isPlaying = false
      this.stopProgressUpdate()
    }

    this.element.setAttribute('data-progress-dragging', 'true')

    const progressHandle = this.shadowRoot.querySelector('.progress-handle')
    if (progressHandle) {
      progressHandle.style.transition = 'none'
      progressHandle.style.opacity = '1'
      progressHandle.style.width = '32px'
      progressHandle.style.height = '32px'

      const currentProgress = videoElement.currentTime / videoElement.duration
      const syncPercentage = currentProgress * 100
      progressHandle.style.left = `${syncPercentage}%`
    }

    this.handleProgressDragMove(e)
  }

  handleProgressDragMove (e) {
    if (!this.isDragging) return

    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement || !this.videoLoaded) return

    e.preventDefault()
    e.stopPropagation()

    const touch = e.touches ? e.touches[0] : e
    const progressContainer = this.shadowRoot.querySelector('.progress-bar-container')
    const rect = progressContainer.getBoundingClientRect()
    const x = touch.clientX - rect.left
    const percentage = Math.max(0, Math.min(1, x / rect.width))
    const seekTime = videoElement.duration * percentage

    videoElement.currentTime = seekTime
    this.updateProgressBar(percentage)
  }

  handleProgressDragEnd () {
    if (!this.isDragging) return

    this.isDragging = false
    this.element.removeAttribute('data-progress-dragging')

    const progressHandle = this.shadowRoot.querySelector('.progress-handle')
    if (progressHandle) {
      progressHandle.style.transition = ''
      progressHandle.style.width = ''
      progressHandle.style.height = ''
      progressHandle.style.opacity = ''
    }

    if (this.wasPlayingBeforeDrag) {
      const videoElement = this.shadowRoot.querySelector('video')
      if (videoElement && videoElement.paused) {
        videoElement.play().then(() => {
          this.isPlaying = true
          this.showPauseState()
          this.startProgressUpdate()
        }).catch(() => {
          this.showPlayState()
        })
      }
    }

    this.wasPlayingBeforeDrag = false
  }

  hideControlsWithDelay () {
    if (this.controlsHideTimeout) {
      clearTimeout(this.controlsHideTimeout)
    }
    this.controlsHideTimeout = setTimeout(() => {
      this.element.classList.add('controls-hidden')
    }, 3000)
  }

  startProgressUpdate () {
    this.stopProgressUpdate()
    const videoElement = this.shadowRoot.querySelector('video')
    if (!videoElement || !this.progressBar) return

    this.updateProgressBar(videoElement.currentTime / videoElement.duration)

    this.progressUpdateInterval = setInterval(() => {
      if (videoElement.ended) {
        this.stopProgressUpdate()
        this.updateProgressBar(0)
        // Reset state to allow replay
        this.isPlaying = false
        this.wasPausedByUser = false
        this.element.setAttribute('data-was-paused-by-user', 'false')
        this.showPlayState()
        this.showPosterImage()
        this.element.classList.add('video-not-started')

        // Dispatch video ended event
        this.element.dispatchEvent(new CustomEvent('videoStateChange', {
          detail: { state: 'ended' },
        }))
      } else {
        this.updateProgressBar(videoElement.currentTime / videoElement.duration)
      }
    }, 16.67)
  }

  stopProgressUpdate () {
    if (this.progressUpdateInterval) {
      clearInterval(this.progressUpdateInterval)
      this.progressUpdateInterval = null
    }
  }

  updateProgressBar (progress) {
    if (this.progressBar) {
      this.progressBar.style.transform = `scaleX(${progress})`
    }

    if (!this.isDragging) {
      const progressHandle = this.shadowRoot.querySelector('.progress-handle')
      if (progressHandle) {
        const percentage = Math.max(0, Math.min(100, progress * 100))
        progressHandle.style.left = `${percentage}%`
      }
    }
  }

  showLoadingState () {
    this.shadowRoot.querySelector('.loading-icon')?.classList.remove('hidden')
    this.shadowRoot.querySelector('.play-icon')?.classList.add('hidden')
    this.shadowRoot.querySelector('.pause-icon')?.classList.add('hidden')
  }

  showPlayState () {
    this.shadowRoot.querySelector('.loading-icon')?.classList.add('hidden')
    this.shadowRoot.querySelector('.play-icon')?.classList.remove('hidden')
    this.shadowRoot.querySelector('.pause-icon')?.classList.add('hidden')

    const controlBtn = this.shadowRoot.querySelector('.video-control-btn')
    if (controlBtn) {
      controlBtn.setAttribute('aria-label', 'Play video')
    }
  }

  showPauseState () {
    this.shadowRoot.querySelector('.loading-icon')?.classList.add('hidden')
    this.shadowRoot.querySelector('.play-icon')?.classList.add('hidden')
    this.shadowRoot.querySelector('.pause-icon')?.classList.remove('hidden')

    const controlBtn = this.shadowRoot.querySelector('.video-control-btn')
    if (controlBtn) {
      controlBtn.setAttribute('aria-label', 'Pause video')
    }
  }

  hidePosterImage () {
    const posterImg = this.shadowRoot.querySelector('.video-poster')
    if (posterImg) {
      posterImg.style.opacity = '0'
      setTimeout(() => {
        if (posterImg.style.opacity === '0') {
          posterImg.style.display = 'none'
        }
      }, 300)
    }
  }

  showPosterImage () {
    const posterImg = this.shadowRoot.querySelector('.video-poster')
    if (posterImg) {
      posterImg.style.opacity = '1'
      posterImg.style.display = 'block'
    }
  }

  updateMuteButton () {
    const volumeOnIcon = this.shadowRoot.querySelector('.volume-on')
    const volumeOffIcon = this.shadowRoot.querySelector('.volume-off')
    const muteBtn = this.shadowRoot.querySelector('.mute-btn')

    if (volumeOnIcon && volumeOffIcon && muteBtn) {
      if (this.isMuted) {
        volumeOnIcon.classList.add('hidden')
        volumeOffIcon.classList.remove('hidden')
        muteBtn.setAttribute('aria-label', 'Unmute video')
      } else {
        volumeOnIcon.classList.remove('hidden')
        volumeOffIcon.classList.add('hidden')
        muteBtn.setAttribute('aria-label', 'Mute video')
      }
    }
  }

  destroy () {
    this.stopProgressUpdate()
    if (this.controlsHideTimeout) {
      clearTimeout(this.controlsHideTimeout)
    }

    // Clean up document-level drag listeners
    document.removeEventListener('touchmove', this.handleProgressDragMove.bind(this))
    document.removeEventListener('touchend', this.handleProgressDragEnd.bind(this))
    document.removeEventListener('mousemove', this.handleProgressDragMove.bind(this))
    document.removeEventListener('mouseup', this.handleProgressDragEnd.bind(this))
  }
}

if (!customElements.get('modal-video')) {
  customElements.define('modal-video', ModalVideo)
}
